import React, { useState } from "react";
import { Divider, Radio } from "@chakra-ui/react";
import { Flex, Form, Modal, RadioGroup } from "components";
import { useCurrentCase } from "duck";
import { SpeciesEnum } from "requests";
import {
  ToothDiagnosisInputs,
  ToothDiagnosisRadio
} from "../../../../../../../shared";
import { Header, Other } from "./components";
import {
  Charts,
  FormValues,
  FRACTURE_OPTIONS,
  FURCATION_OPTIONS,
  getDefaultValues,
  handleChartChange,
  MOBILITY_OPTIONS,
  RESORPTION_OPTIONS,
  TOOTH_STATE_OPTIONS,
  useUpdateQuickChart
} from "./duck";

interface QuickChartProps {
  selection: Charts;
  onClose(): void;
}

const QuickChart: React.FC<QuickChartProps> = ({ selection, onClose }) => {
  const { dentalChartings, species, milkOrAdultTeeth } = useCurrentCase(
    medicalCase => ({
      dentalChartings: medicalCase.data.client.patient.dentalChartings || [],
      species: medicalCase.data.client.patient.species || null,
      milkOrAdultTeeth:
        medicalCase.data.client.patient.generalAssessment?.dentalChart || null
    })
  );

  const [currentToothNumber, setCurrentToothNumber] = useState(
    selection[0].tooth
  );
  const currentChart =
    dentalChartings.find(chart => chart.tooth === currentToothNumber) || null;

  const defaultValues = getDefaultValues(currentChart);

  const { next, prev } = handleChartChange(
    species,
    milkOrAdultTeeth,
    selection,
    currentChart,
    dentalChartings
  );
  const handleFormChange = useUpdateQuickChart(selection, currentChart);

  return defaultValues ? (
    <Modal
      size="3xl"
      title={
        <Header
          charts={{ current: currentChart, prev, next }}
          handleChartChange={chart =>
            chart && setCurrentToothNumber(chart.tooth)
          }
          selection={selection}
        />
      }
      isOpen
      onClose={onClose}
    >
      <Form<FormValues> defaultValues={defaultValues}>
        <Form.Watcher
          defaultValues={defaultValues}
          onChange={handleFormChange}
        />
        <Flex gridGap="24px" flexDirection="column">
          <ToothDiagnosisInputs
            label="Periodontal Pocket (mm)"
            namePrefix="periodontalPocket"
          />
          <Divider h="1px" borderColor="#D0D1D3" />
          <ToothDiagnosisInputs
            label="Gingival Recession (mm)"
            namePrefix="gingivalRecession"
          />
        </Flex>
        <Divider my={6} h="1px" borderColor="#D0D1D3" />
        <Flex justify="space-between" flexWrap="wrap">
          <ToothDiagnosisRadio
            label="Mobility"
            name="mobility"
            options={MOBILITY_OPTIONS}
          />

          <ToothDiagnosisRadio
            label="Furcation"
            name="furcation"
            options={FURCATION_OPTIONS}
          />

          {species === SpeciesEnum.Dog && (
            <ToothDiagnosisRadio
              label="Fractures"
              name="fracture"
              options={FRACTURE_OPTIONS}
              columns={2}
              rows={4}
            />
          )}

          {(species === SpeciesEnum.Cat || species === SpeciesEnum.Rabbit) && (
            <ToothDiagnosisRadio
              label="Tooth Resorption"
              name="resorption"
              options={RESORPTION_OPTIONS}
              columns={2}
            />
          )}

          {species === SpeciesEnum.Dog && <Other />}
        </Flex>
        <Divider my={6} h="1px" borderColor="#D0D1D3" />
        <RadioGroup
          name="toothState"
          stackProps={{ justify: "space-between", spacing: 6 }}
        >
          {TOOTH_STATE_OPTIONS.map(opt => (
            <Radio key={opt.value} value={opt.value}>
              {opt.label}
            </Radio>
          ))}
        </RadioGroup>
      </Form>
    </Modal>
  ) : null;
};

export default QuickChart;
